import React from "react";

import type { AcuityApprovalRequest, AcuityScore, UUID } from "@/core/types";
import { AcuityScoreChange } from "@/features/acuity/components/AcuityScoreChange";
import { AlertBar2 } from "@/ui2/atoms/AlertBar";
import dayjs from "@/lib/helpers/dayjs";
import { Heading2 } from "@/ui2/atoms/Heading";

import { PatientData } from "../../components/PatientData";

import { ApproveAcuityModal } from "./ApproveAcuityModal";
import { RejectAcuityModal } from "./RejectAcuityModal";

type AcuityUpdateAlertProps = AcuityApprovalRequest & {
  currentScore: AcuityScore;
  patientId: UUID;
};

export function AcuityUpdateAlert({
  currentScore,
  patientId,
  ...approvalRequest
}: AcuityUpdateAlertProps) {
  const { additional_details, entity_id, reasoning, requested_at, requestor_name, score } =
    approvalRequest;
  const modalProps = { entity_id, patientId };

  return (
    <AlertBar2
      className="tw:grid tw:gap-3.5 tw:p-4 tw:lg:items-center tw:lg:justify-between"
      variant="warning"
    >
      <Heading2 className="tw:text-text-warning-primary" size={2}>
        Acuity override pending approval
      </Heading2>

      <dl className="tw:flex tw:flex-col tw:gap-2.5 tw:lg:col-start-1 tw:lg:row-start-2">
        <PatientData label="Requested by">
          {requestor_name} · {dayjs(requested_at).format("M/D/YYYY, h:mm A")}
        </PatientData>

        <PatientData classNameChildren="tw:inline-flex tw:items-center" label="Change">
          <AcuityScoreChange currentScore={currentScore} isLabelled newScore={score} />
        </PatientData>

        <PatientData label="Reasoning">{reasoning}</PatientData>

        {additional_details && (
          <PatientData label="Additional Details">{additional_details}</PatientData>
        )}
      </dl>

      <div className="tw:flex tw:justify-end tw:gap-3 tw:lg:col-start-2 tw:lg:row-span-2">
        <RejectAcuityModal {...modalProps} />
        <ApproveAcuityModal {...modalProps} />
      </div>
    </AlertBar2>
  );
}
